import { ReactNode } from 'react';
import { Box, Paper, Typography, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';

/** Section heading (rendered as <h2>) for legal documents. */
export function LegalSection({ children }: { children: string }) {
  return (
    <Typography variant="h5" component="h2">
      {children}
    </Typography>
  );
}

/** Body paragraph for legal documents. */
export function LegalParagraph({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body1" component="p">
      {children}
    </Typography>
  );
}

interface LegalPageLayoutProps {
  /** Document title, rendered as the page <h1>. */
  title: string;
  /** Human-readable "last updated" date (e.g. "16 June 2026"). */
  lastUpdated: string;
  /** The legal document body (sections with <h2> headings). */
  children: ReactNode;
}

/**
 * Shared chrome for the legal documents (Terms, Privacy, DPA): page title,
 * last-updated line, the pending-legal-review draft banner, and a readable
 * single-column container. The document body is passed as children.
 *
 * Legal copy is maintained as whole English documents (the source of truth for
 * counsel review), not as per-string i18n keys; only the surrounding chrome is
 * translated.
 */
function LegalPageLayout({ title, lastUpdated, children }: LegalPageLayoutProps) {
  const { t } = useTranslation();

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto' }}>
      <Typography variant="h3" component="h1" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {t('legal.lastUpdated', { date: lastUpdated })}
      </Typography>

      <Alert severity="info" sx={{ my: 2 }}>
        {t('legal.draftBanner')}
      </Alert>

      <Paper
        sx={{
          p: { xs: 2, sm: 4 },
          '& h2': { mt: 4, mb: 1 },
          '& h3': { mt: 3, mb: 1 },
          '& p': { mb: 2 },
          '& ul': { mb: 2, pl: 3 },
          '& li': { mb: 0.5 },
        }}
      >
        {children}
      </Paper>
    </Box>
  );
}

export default LegalPageLayout;
